import { Inject, Injectable } from "@angular/core";
import { AbstractService } from "./abstract-service.service";
import { Order } from "../Models/order";
import { BehaviorSubject, Observable } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { APP_CONFIG, AppConfig } from "./config";
import { Response } from "../Models/response";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { DeliveryAddress } from "../Models/DeliveryAddress";
import { Page } from "../Models/page";
import { GuestOrderTO } from "../Models/guestOrderTO";
import { Discount } from "../Models/discount";
import { AccountService } from "./account.service";
import { MobileOrder } from "../Models/mobileOrder";

@Injectable({
  providedIn: "root",
})
export class OrderService extends AbstractService<Order, number> {
  url: string;
  url2: string;

  result: any;
  _productPrice?: number;

  get productPrice(): number {
    return this._productPrice!;
  }

  set productPrice(value: number) {
    this._productPrice = value;
  }

  _discount?: Discount;

  get discount(): Discount {
    return this._discount!;
  }

  set discount(value: Discount) {
    this._discount = value;
  }

  private _orderSubject: BehaviorSubject<Page<Order> | null> =
    new BehaviorSubject<Page<Order> | null>(null);
  constructor(
    http: HttpClient,
    @Inject(APP_CONFIG) config: AppConfig,
    private accountService: AccountService,
  ) {
    super(http);
    this.url = `${config.apiEndpoint}/new/orders`;
    this.url2 = `${config.apiEndpoint}/orderSplits`;

    this.accountService.accountSubject.subscribe((client) => {
      if (client) {
        // ✅ getByClientId renvoie directement un Page<Order>
        this.getByClientId(1, 5, "id", "desc", "").subscribe(
          (pageOfOrders: Page<Order>) => {
            if (pageOfOrders) {
              this._orderSubject.next(pageOfOrders);
            }
          },
        );
      } else {
        this._orderSubject.next(null);
      }
    });
  }

  public changeState(orderId: number, state: string, name: string) {
    const url = `${this.url}/changeStateOptimized/${orderId}/${state}`;
    let params = new HttpParams();
    params = params.append("name", name);
    const options = { params };
    const res = this.http
      .post<Response<string>>(url, options)
      .pipe(map(({ body }) => body));
    return res;
  }

  public changePaymentState(orderId: number, state: string, name: string) {
    const url = `${this.url}/updatePaymentState/${orderId}/${state}`;
    const res = this.http
      .post<Response<string>>(url, {})
      .pipe(map(({ body }) => body));
    return res;
  }

  public getByClientId(
    pageNo: number,
    pageSize: number,
    sortBy: string,
    direction: string,
    state: string,
    filters: { startDate?: string; endDate?: string; search?: string } = {},
  ): Observable<Page<Order>> {
    const url = `${this.url}/client/`;
    let params = new HttpParams()
      .set("pageNo", pageNo.toString())
      .set("pageSize", pageSize.toString())
      .set("sortBy", sortBy)
      .set("direction", direction)
      .set("state", state);
    // creation date range (yyyy-MM-dd, inclusive) and order id search (partial match)
    if (filters.startDate) params = params.set("startDate", filters.startDate);
    if (filters.endDate) params = params.set("endDate", filters.endDate);
    if (filters.search) params = params.set("search", filters.search);

    const options = { params, withCredentials: true };

    return this.http.get<Page<Order>>(url, options).pipe(tap((response) => { }));
  }

  public getAllOrdersInMobile(
    pageNo: number,
    pageSize: number,
    sortBy: string,
    direction: string,
    selectedStatus: string,
  ): Observable<Response<Page<MobileOrder>>> {
    const url = `${this.url}/mobile/client`;
    let params = new HttpParams()
      .set("pageNo", pageNo.toString())
      .set("pageSize", pageSize.toString())
      .set("sortBy", sortBy)
      .set("direction", direction)
      .set("state", selectedStatus);
    return this.http.get<Response<Page<MobileOrder>>>(url, { params }).pipe(
      filter(
        (response: Response<Page<MobileOrder>>) =>
          response.body !== null && response.body !== undefined,
      ),
      map((response: Response<Page<MobileOrder>>) => response),
    );
  }

  public getOrderById(orderId: number): Observable<Order> {
    const url = `${this.url}/guichet/client/${orderId}`;

    return this.http.get<Order>(url, { withCredentials: true }).pipe(
      tap((response) => console.log("Réponse du serveur:", response)),
      map((response: Order) => response), // ou même: just remove map()
    );
  }

  public placeOrder(
    clientId: number,
    pointOfSaleId: number,
    phone: string,
    email: string,
    salary: number,
    configurationsList: any,
    numberOfMonths: number,
    clientDate: any,
    paymentType: string,
    totalPayment: number,
  ): Observable<Order> {
    const url = `${this.url}`;
    const entity = {
      clientId: clientId,
      configurationsdetails: configurationsList,
      pointOfSaleId: pointOfSaleId,
      dateOfBirthSimulation: clientDate,
      salarySimulation: salary,
      email: email,
      phone: phone,
      numberOfMonths: numberOfMonths,
      paymentType: paymentType,
      totalPayment: totalPayment,
    };

    return this.http.post<number>(url, entity).pipe(
      switchMap((id) => {
        if (!id) {
          throw new Error("Order ID not found in response");
        }
        return this.getOrderById(id);
      }),
      tap((order) => {
        const currentValue = this._orderSubject.value;
        if (currentValue) {
          const updatedOrders = [...currentValue.content, order];
          this._orderSubject.next({ ...currentValue, content: updatedOrders });
        }
      }),
    );
  }

  /**
   * Creates a guest order (no client account) and returns the created order id.
   * Sent with credentials: the session is what later allows downloading its purchase order.
   */
  public directOrder(order: GuestOrderTO): Observable<number> {
    const url = `${this.url}/create/guest`;
    return this.http.post<number>(url, order, { withCredentials: true });
  }

  public updateOrderPayMethod(
    orderId: number,
    paymentMethod: string,
  ): Observable<any> {
    const params = new HttpParams()
      .set("paymentMethod", paymentMethod)
      .set("orderId", orderId.toString());

    const url = `${this.url}/guichet/paymentMethod`;
    return this.http.patch<Response<Order>>(url, params).pipe(
      map((res) => {
        if (res) {
          const orders = [
            ...this._orderSubject.value!.content,
            res.body,
          ].filter((order) => order !== undefined) as Order[];
          var newValue = this._orderSubject.value;
          newValue!.content = orders;
          this._orderSubject.next(newValue);
          return res.body;
        }
      }),
    );
  }

  public isorderpossible(
    clientId: number,
    paymentMethod: string,
    address: DeliveryAddress,
    deliveryType: string,
  ): Observable<any> {
    const url = `${this.url}/isorderpossible`;
    const entity = {
      clientId: clientId,
      paymentMethod: paymentMethod,
      address: address,
      livreurId: 1,
      deliveryType,
    };
    return this.http.post<Response<Record<string, any>>>(url, entity).pipe(
      map((res) => {
        if (res.body) {
          return res.body;
        }
      }),
    );
  }

  // test if an order contains a physicalProducts or Not
  public isContainPhysicalProduct(clientId: number): Observable<any> {
    const url = `${this.url}/iscontainphysical/${clientId}`;
    return this.http.get<Response<Record<string, any>>>(url).pipe(
      map((res) => {
        if (res.body) {
          return res.body;
        }
      }),
    );
  }

  public cancelOrder(orderId: number): Observable<Response<string>> {
    const url = `${this.url}/cancel/${orderId}`;
    return this.http.patch<Response<string>>(url, null);
  }

  get orderSubject(): BehaviorSubject<Page<Order> | null> {
    return this._orderSubject;
  }

  public addPaymentReceipt(
    file: File,
    comment: string,
    idOrder: number,
  ): Observable<string> {
    const url = `${this.url}/guichet/addPaymentReceipt/${idOrder}`;
    const formData = new FormData();
    formData.append("files", file, file.name);
    if (comment) {
      formData.append("comment", comment);
    }

    // the API answers 200 with a plain-text message, even when the upload fails
    return this.http
      .put(url, formData, { withCredentials: true, responseType: "text" })
      .pipe(
        map((message: string) => {
          if (message === "Payment receipt uploaded successfully.") {
            return message;
          }
          throw new Error(message);
        }),
      );
  }

  async delay(ms: number) {
    await new Promise<void>((resolve) => setTimeout(() => resolve(), ms)).then(
      () => console.log(""),
    );
  }

  getNewTransaction(orderId: number) {
    const url = `${this.url}/addNewTransaction`;

    const params = new HttpParams().set("orderId", orderId.toString());

    return this.http
      .patch<Response<Record<string, any>>>(url, {}, { params })
      .pipe(
        map((res) => {
          if (res.body) {
            return res.body;
          }
        }),
      );
  }

  public downloadOrderBill(id: number): Observable<Blob> {
    const url = `${this.url}/downloadOrderBill/${id}`;

    return this.http.get(url, {
      withCredentials: true,
      responseType: "blob",
    });
  }

  public downloadGuestOrderBill(id: number): Observable<any> {
    const url = `${this.url}/guest/download-order-bill/${id}`;
    return this.http.get<Blob>(url, {
      withCredentials: true,
      responseType: "blob" as "json",
    });
  }

  public downloadSimulationPdf(
    idClient?: number,
    pointOfSaleId?: number,
    financedAmount?: number,
    monthlyPayment?: number,
    numberOfMonths?: number,
    reimbursementAmount?: number,
    totalMarginAmount?: number,
    phoneNumber?: string,
  ): Observable<Blob> {
    const url = `${this.url2}/simulation`;

    const entity = {
      financedAmount,
      idClient,
      monthlyPayment,
      numberOfMonths,
      phoneNumber,
      pointOfSaleId,
      reimbursementAmount,
      totalMarginAmount,
    };

    return this.http.post<Blob>(url, entity, {
      responseType: "blob" as "json", // 👈 obligatoire pour éviter l'erreur de typage
    });
  }
  sendOrderBillToCurrentClient(orderId: number) {
    const url = `${this.url}/SendOrderBillEmail/${orderId}`;
    return this.http.get<Response<Record<string, any>>>(url).pipe(
      map((res) => {
        return res;
      }),
    );
  }

  createOrder(orderData: any): Observable<any> {
    const url = `${this.url}/create`;
    return this.http.post(url, orderData, { withCredentials: true });
  }

  //the response contains order details
  createFetchOrder(orderData: any): Observable<any> {
    const url = `${this.url}/create-and-fetch`;
    return this.http.post(url, orderData, { withCredentials: true });
  }
}
